import { useEffect, useState } from "react";
import { api, errMsg } from "@/lib/api";
import { Spinner } from "@/components/bits";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Database, Loader2, PlugZap, Siren } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { EMERG_KEYS } from "@/components/EmergencyMonitor";
import { toast } from "sonner";

const EMPTY = { brave_api_url: "", brave_origin: "", brave_api_key: "" };

export default function TenantSettingsDialog({ open, onOpenChange }) {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [meta, setMeta] = useState(null);
  const [form, setForm] = useState(EMPTY);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    api.get("/tenant/settings")
      .then(({ data }) => {
        setMeta(data);
        setForm({ ...EMPTY, brave_api_url: data.brave_api_url || "", brave_origin: data.brave_origin || "" });
      })
      .catch((e) => toast.error(errMsg(e)))
      .finally(() => setLoading(false));
  }, [open]);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { brave_api_url: form.brave_api_url, brave_origin: form.brave_origin };
      if (form.brave_api_key) payload.brave_api_key = form.brave_api_key;
      const { data } = await api.patch("/tenant/settings", payload);
      setMeta(data);
      setForm((f) => ({ ...f, brave_api_key: "" }));
      toast.success("Configurações salvas");
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setSaving(false);
    }
  };

  const test = async () => {
    setTesting(true);
    try {
      const { data } = await api.post("/tenant/settings/test");
      if (data.ok) toast.success(`Conexão estabelecida${data.empreendimento ? ` — ${data.empreendimento}` : ""}`, { id: "brave-test" });
      else toast.error(data.detail || "Falha na conexão", { id: "brave-test" });
    } catch (err) {
      toast.error(errMsg(err), { id: "brave-test" });
    } finally {
      setTesting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-zinc-900 border-zinc-800 text-zinc-100 max-h-[90vh] overflow-y-auto" data-testid="tenant-settings-dialog">
        <DialogHeader>
          <DialogTitle className="text-lg font-medium">Configurações</DialogTitle>
          <DialogDescription className="text-sm text-zinc-400">
            Integrações e base de dados da organização.
          </DialogDescription>
        </DialogHeader>
        {loading ? <Spinner /> : (<>
          <div className="rounded-md border border-zinc-800 bg-zinc-950 p-4" data-testid="brave-database-section">
            <div className="flex items-center gap-2 mb-1">
              <Database className="h-4 w-4 text-indigo-400" />
              <h3 className="text-sm font-medium text-zinc-100">Base de Dados — Brave Access</h3>
            </div>
            <p className="text-xs text-zinc-500 mb-4">
              Credenciais da API de controle de acesso (empreendimento, EQLs, locais e moradores). A chave é armazenada criptografada.
            </p>
            <form onSubmit={save} className="space-y-3" data-testid="brave-settings-form">
              <div>
                <Label className="text-xs text-zinc-400">URL base da API</Label>
                <Input data-testid="brave-api-url-input" value={form.brave_api_url} maxLength={300}
                  onChange={(e) => setForm({ ...form, brave_api_url: e.target.value })}
                  placeholder="https://brave-access-5.emergent.host/api"
                  className="mt-1.5 bg-zinc-900 border-zinc-800 text-zinc-100 placeholder:text-zinc-600 rounded-md focus:border-indigo-500" />
              </div>
              <div>
                <Label className="text-xs text-zinc-400">Código do empreendimento (origem)</Label>
                <Input data-testid="brave-origin-input" value={form.brave_origin} maxLength={120}
                  onChange={(e) => setForm({ ...form, brave_origin: e.target.value })}
                  placeholder="Ex.: código do condomínio no Brave Access"
                  className="mt-1.5 bg-zinc-900 border-zinc-800 text-zinc-100 placeholder:text-zinc-600 rounded-md focus:border-indigo-500" />
              </div>
              <div>
                <Label className="text-xs text-zinc-400">Chave de API</Label>
                <Input data-testid="brave-api-key-input" type="password" value={form.brave_api_key} maxLength={300}
                  autoComplete="new-password"
                  onChange={(e) => setForm({ ...form, brave_api_key: e.target.value })}
                  placeholder={meta?.has_api_key ? `Configurada (${meta.api_key_hint || "••••"}) — deixe em branco para manter` : "Cole a chave de API"}
                  className="mt-1.5 bg-zinc-900 border-zinc-800 text-zinc-100 placeholder:text-zinc-600 rounded-md focus:border-indigo-500" />
              </div>
              <div className="flex justify-end gap-2 pt-1">
                <Button type="button" data-testid="brave-test-button" variant="outline" disabled={testing || saving}
                  onClick={test}
                  className="border-zinc-700 text-zinc-300 hover:bg-zinc-800 hover:text-zinc-100 rounded-md">
                  {testing ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <PlugZap className="h-4 w-4 mr-1.5" />}
                  Testar conexão
                </Button>
                <Button data-testid="brave-save-button" type="submit" disabled={saving || testing}
                  className="bg-indigo-600 hover:bg-indigo-500 text-white rounded-md transition-colors duration-200">
                  {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Salvar"}
                </Button>
              </div>
            </form>
          </div>
          <EmergMonitorSection />
          </>)}
      </DialogContent>
    </Dialog>
  );
}

function EmergMonitorSection() {
  const [on, setOn] = useState(() => localStorage.getItem(EMERG_KEYS.enabled) === "1");
  const [intervalo, setIntervalo] = useState(() => localStorage.getItem(EMERG_KEYS.interval) || "10");

  const toggle = (v) => {
    setOn(v);
    localStorage.setItem(EMERG_KEYS.enabled, v ? "1" : "0");
    toast.success(v ? "Monitoramento ativado neste computador" : "Monitoramento desativado neste computador");
  };

  const mudarIntervalo = (v) => {
    setIntervalo(v);
    const n = Math.max(5, Number(v) || 10);
    localStorage.setItem(EMERG_KEYS.interval, String(n));
  };

  return (
    <div className="rounded-md border border-zinc-800 bg-zinc-950 p-4 mt-4" data-testid="emerg-monitor-section">
      <div className="flex items-center gap-2 mb-1">
        <Siren className="h-4 w-4 text-red-400" />
        <h3 className="text-sm font-medium text-zinc-100">Monitoramento de Emergências</h3>
      </div>
      <p className="text-xs text-zinc-500 mb-4">
        Serviço local: este computador passa a verificar a fila de emergências do app mobile e abre o alerta na tela.
        A ativação vale apenas para este PC (não é global).
      </p>
      <div className="flex items-center justify-between gap-3">
        <Label htmlFor="emerg-monitor-switch" className="text-xs text-zinc-400">Ativar neste computador</Label>
        <Switch id="emerg-monitor-switch" data-testid="emerg-monitor-switch" checked={on} onCheckedChange={toggle} />
      </div>
      <div className="mt-3">
        <Label className="text-xs text-zinc-400">Intervalo de checagem (segundos, mínimo 5)</Label>
        <Input type="number" min={5} data-testid="emerg-monitor-interval" value={intervalo}
          onChange={(e) => mudarIntervalo(e.target.value)}
          className="mt-1.5 bg-zinc-900 border-zinc-800 text-zinc-100 rounded-md focus:border-indigo-500 w-28" />
      </div>
    </div>
  );
}
